import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, MessageCircle, CheckCircle2, ChevronLeft, ChevronRight, BookOpen, ReceiptText, Send, Smartphone } from "lucide-react";
import { WA_NUMBER_1, WA_NUMBER_2, WA_PAID_MSG, waLink, trackEvent, PREVIEW_PAGES, UPI_LINK } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentModal = () => {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const [proofOpen, setProofOpen] = useState(false);
  const [proof, setProof] = useState({ name: "", phone: "", txn_id: "" });
  const [sending, setSending] = useState(false);

  const submitProof = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/payment-proofs`, proof);
      trackEvent("payment_proof", { source: "payment-modal" });
      toast.success("Payment proof received! We'll verify and WhatsApp your download link.");
      setProof({ name: "", phone: "", txn_id: "" });
      setProofOpen(false);
    } catch {
      toast.error("Could not submit. Please WhatsApp us your screenshot instead.");
    } finally {
      setSending(false);
    }
  };

  const flip = (dir) => {
    setPage((p) => {
      const next = (p + dir + PREVIEW_PAGES.length) % PREVIEW_PAGES.length;
      trackEvent("preview_flip", { source: "payment-modal", number: String(next + 1) });
      return next;
    });
  };

  useEffect(() => {
    const show = () => { setPage(0); setZoomed(false); setOpen(true); };
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setZoomed((z) => {
        if (!z) setOpen(false);
        return false;
      });
    };
    window.addEventListener("iqmize:open-payment", show);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("iqmize:open-payment", show);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      data-testid="payment-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm px-5"
      onClick={() => setOpen(false)}
    >
      <div
        className="relative w-full max-w-md rounded-3xl bg-white p-7 lg:p-9 shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => setOpen(false)}
          data-testid="payment-modal-close"
          aria-label="Close payment popup"
          className="absolute top-4 right-4 flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition-colors duration-200 hover:bg-slate-200 hover:text-slate-800"
        >
          <X size={18} />
        </button>

        <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand">IQMIZE Book — Digital PDF</p>
        <div className="mt-3 flex items-end gap-3">
          <span className="text-4xl font-extrabold text-slate-900">₹49</span>
          <span className="pb-1 text-lg text-slate-400 line-through">₹780</span>
          <span className="pb-1 rounded-full bg-igold px-3 py-1 text-xs font-extrabold uppercase tracking-wide text-slate-900">
            94% off
          </span>
        </div>

        <div className="mt-6">
          <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-brand">
            <BookOpen size={14} /> Flip through sample pages
          </p>
          <div className="relative mt-3 rounded-2xl border border-slate-200 bg-brand-light/50 p-2" data-testid="preview-viewer">
            <img
              src={PREVIEW_PAGES[page].src}
              alt={`Sample page: ${PREVIEW_PAGES[page].label}`}
              data-testid="preview-page-image"
              onClick={() => setZoomed(true)}
              title="Tap to read full-screen"
              className="mx-auto w-full max-h-[320px] object-contain rounded-xl cursor-zoom-in"
              loading="lazy"
            />
            <button
              type="button"
              onClick={() => flip(-1)}
              data-testid="preview-prev-btn"
              aria-label="Previous sample page"
              className="absolute left-3 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-inavy shadow-md transition-transform duration-200 hover:scale-110"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => flip(1)}
              data-testid="preview-next-btn"
              aria-label="Next sample page"
              className="absolute right-3 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-inavy shadow-md transition-transform duration-200 hover:scale-110"
            >
              <ChevronRight size={18} />
            </button>
          </div>
          <div className="mt-2.5 flex items-center justify-between">
            <p className="text-xs font-bold text-slate-600" data-testid="preview-page-label">
              {PREVIEW_PAGES[page].label}
            </p>
            <div className="flex gap-1.5" data-testid="preview-dots">
              {PREVIEW_PAGES.map((p, i) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => { setPage(i); trackEvent("preview_flip", { source: "payment-modal", number: String(i + 1) }); }}
                  aria-label={`Go to ${p.label}`}
                  data-testid={`preview-dot-${i + 1}`}
                  className={`h-2 rounded-full transition-[width,background-color] duration-300 ${
                    i === page ? "w-5 bg-brand" : "w-2 bg-slate-300 hover:bg-brand/50"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <a
            href={UPI_LINK}
            onClick={() => trackEvent("upi_deeplink_click", { source: "payment-modal-qr" })}
            data-testid="qr-pay-link"
            aria-label="Tap to pay ₹49 via UPI"
            className="block"
          >
            <img
              src="/assets/upi-qr.png"
              alt="UPI QR code to pay ₹49 for the IQMIZE book — tap to pay"
              data-testid="payment-modal-qr"
              className="mx-auto w-full max-w-[280px] object-contain"
            />
          </a>
          <a
            href={UPI_LINK}
            onClick={() => trackEvent("upi_deeplink_click", { source: "payment-modal-button" })}
            data-testid="upi-pay-btn"
            className="mt-3 flex w-full items-center justify-center gap-2.5 rounded-full bg-igold px-6 py-3.5 text-sm font-extrabold text-slate-900 shadow-md transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-0.5 hover:bg-igold-hover hover:shadow-lg"
          >
            <Smartphone size={18} /> Tap to Pay ₹49 via UPI
          </a>
          <p className="mt-2 text-center text-[11px] font-semibold text-slate-400">
            Opens GPay, PhonePe, Paytm or any UPI app with the amount filled in
          </p>
        </div>

        <div className="mt-5 space-y-2.5" data-testid="payment-modal-instructions">
          {[
            "Scan the QR with any UPI app and pay ₹49.",
            "Take a screenshot of the payment success screen.",
            "WhatsApp us the screenshot — get your download link instantly.",
          ].map((step, i) => (
            <div key={step} className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand text-[11px] font-extrabold text-white">
                {i + 1}
              </span>
              <p className="text-sm font-semibold text-slate-700">{step}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 space-y-3">
          <a
            href={waLink(WA_NUMBER_1, WA_PAID_MSG)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("whatsapp_click", { source: "payment-modal", number: WA_NUMBER_1 })}
            data-testid="payment-modal-whatsapp-1"
            className="flex w-full items-center justify-center gap-2.5 rounded-full bg-brand px-6 py-3.5 text-sm font-bold text-white shadow-md transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-0.5 hover:bg-brand-dark hover:shadow-lg"
          >
            <MessageCircle size={18} /> WhatsApp 70346 18014
          </a>
          <a
            href={waLink(WA_NUMBER_2, WA_PAID_MSG)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("whatsapp_click", { source: "payment-modal", number: WA_NUMBER_2 })}
            data-testid="payment-modal-whatsapp-2"
            className="flex w-full items-center justify-center gap-2.5 rounded-full border-2 border-brand bg-white px-6 py-3.5 text-sm font-bold text-brand transition-colors duration-200 hover:bg-brand-light"
          >
            <MessageCircle size={18} /> WhatsApp 85905 66268
          </a>
        </div>

        <div className="mt-5 border-t border-slate-100 pt-5">
          <button
            type="button"
            onClick={() => setProofOpen(!proofOpen)}
            data-testid="proof-toggle-btn"
            className="flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-brand-light/60 px-5 py-3 text-sm font-bold text-inavy transition-colors duration-200 hover:bg-brand-light"
          >
            <ReceiptText size={16} /> Already paid? Submit payment proof
          </button>

          {proofOpen && (
            <form onSubmit={submitProof} data-testid="proof-form" className="mt-4 space-y-3">
              <input
                required
                data-testid="proof-name-input"
                placeholder="Your name"
                value={proof.name}
                onChange={(e) => setProof({ ...proof, name: e.target.value })}
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-800 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
              />
              <input
                required
                type="tel"
                data-testid="proof-phone-input"
                placeholder="WhatsApp number"
                value={proof.phone}
                onChange={(e) => setProof({ ...proof, phone: e.target.value })}
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-800 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
              />
              <input
                required
                minLength={4}
                data-testid="proof-txn-input"
                placeholder="UPI transaction / reference ID"
                value={proof.txn_id}
                onChange={(e) => setProof({ ...proof, txn_id: e.target.value })}
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-800 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="proof-submit-btn"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-inavy px-5 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-inavy-dark disabled:opacity-60"
              >
                <Send size={15} /> {sending ? "Submitting..." : "Submit Proof"}
              </button>
            </form>
          )}
        </div>

        <p className="mt-4 flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-500">
          <CheckCircle2 size={13} className="text-brand" /> Student Workbook + Parent's Guide + Tips &amp; Tools
        </p>
      </div>

      {zoomed && (
        <div
          data-testid="preview-zoom-overlay"
          className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/90 p-4"
          onClick={() => setZoomed(false)}
        >
          <img
            src={PREVIEW_PAGES[page].src}
            alt={`Sample page full-screen: ${PREVIEW_PAGES[page].label}`}
            data-testid="preview-zoom-image"
            className="max-h-full max-w-full object-contain rounded-lg shadow-2xl"
          />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setZoomed(false); }}
            data-testid="preview-zoom-close"
            aria-label="Close full-screen page"
            className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-slate-700 shadow-lg transition-transform duration-200 hover:scale-110"
          >
            <X size={20} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); flip(-1); }}
            data-testid="preview-zoom-prev"
            aria-label="Previous sample page"
            className="absolute left-3 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-white/95 text-inavy shadow-lg transition-transform duration-200 hover:scale-110"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); flip(1); }}
            data-testid="preview-zoom-next"
            aria-label="Next sample page"
            className="absolute right-3 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-white/95 text-inavy shadow-lg transition-transform duration-200 hover:scale-110"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      )}
    </div>
  );
};

export default PaymentModal;
